import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn, renderHtmlChoiceId } from "@/lib/utils";
import { useQuestion } from "@/store/question.context";
import {
  ChoiceType,
  ChoiceValueType,
  FillableChoice,
} from "@/types/questionnaries";
import { PercentageCircle } from "iconsax-react";
import { FC, RefObject, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

type AllRequiredChoiceItemProps = {
  choice: FillableChoice;
  level?: number;
  disabled?: boolean;
};

const AllRequiredChoiceItem: FC<AllRequiredChoiceItemProps> = ({
  choice,
  level = 0,
  disabled,
}) => {
  const { isSuggestionApplied, setIsSuggestionApplied, updateChoice } =
    useQuestion();

  const [answerValue, setAnswerValue] = useState(choice.answerValue || "");

  const [debouncedAnswerValue] = useDebounce(answerValue, 400);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleTextChange = (value: string) => {
    setIsSuggestionApplied(false);
    setAnswerValue(value);
  };

  // Save answer when the debounced value changes. That means
  // when the user stops typing for a moment
  useEffect(() => {
    if (choice.type === ChoiceType.Fillable) {
      updateChoice({
        ...choice,
        selected: !!debouncedAnswerValue,
        answerValue: debouncedAnswerValue,
      });
    }
  }, [debouncedAnswerValue]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = choice.answerValue;
    }
  }, [choice.answerValue]);

  return (
    <li
      className="flex flex-col items-baseline gap-2 lg:flex-row"
      style={{ paddingLeft: `${40 * level}px` }}
    >
      <Label
        className="flex grow flex-col gap-1 font-normal"
        htmlFor={`${renderHtmlChoiceId(choice.id)}-text`}
      >
        <span className="block">{choice.label}</span>
        <span className="block text-[#8090A3]">{choice.statement}</span>
      </Label>

      {choice.valueType === ChoiceValueType.LongText ? (
        <Textarea
          id={`${renderHtmlChoiceId(choice.id)}-text`}
          ref={inputRef as RefObject<HTMLTextAreaElement>}
          onChange={(e) => handleTextChange(e.target.value)}
          className={cn(
            "w-full max-w-sm shrink-0",
            isSuggestionApplied && "border-[#F0A000]",
          )}
          disabled={disabled}
        ></Textarea>
      ) : (
        <div className="relative w-full max-w-xs shrink-0">
          {choice.valueType === ChoiceValueType.Percentage && (
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[#4F5D6D]">
              <PercentageCircle />
            </div>
          )}

          <Input
            id={`${renderHtmlChoiceId(choice.id)}-text`}
            ref={inputRef as RefObject<HTMLInputElement>}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(evt) =>
              choice.valueType !== ChoiceValueType.Text &&
              ["e", "E", "+", "-"].includes(evt.key) &&
              evt.preventDefault()
            }
            defaultValue={answerValue}
            className={cn(
              choice.valueType === ChoiceValueType.Percentage && "pr-11",
              choice.valueType !== ChoiceValueType.Text && "text-right",
            )}
            type={choice.valueType === ChoiceValueType.Text ? "text" : "number"}
            step={choice.valueType === ChoiceValueType.Integer ? 1 : undefined}
            min={choice.valueType === ChoiceValueType.Text ? undefined : 0}
            disabled={disabled}
          />
        </div>
      )}
    </li>
  );
};

export default AllRequiredChoiceItem;
